import { Timepicker, TimepickerEvents } from "@dhx/ts-timepicker";
import { create, el } from "@dhx/ts-common/dom";
import { Label } from "./helper/label";
import { Popup, PopupEvents } from "@dhx/ts-popup";
import { IEventSystem, EventSystem } from "@dhx/ts-common/events";
import { ValidationStatus, ITimeInput, IBaseTimepickerEventHandlersMap, BaseElementEvent } from "../types";
import { getFormItemCss, getValidationMessage, isTimeFormat, isEmptyObj } from "../helper";

export class TimeInput extends Label {
	public config: ITimeInput;
	public timepicker: Timepicker;
	public events: IEventSystem<BaseElementEvent, IBaseTimepickerEventHandlersMap> = new EventSystem();
	private _popup: Popup;

	constructor(container, config: ITimeInput) {
		super(null, config);

		this.setConfig(config);
		const render = () => this._draw();
		this.mount(container, create({ render }));
	}
	validate(): boolean {
		const { required, value, validation } = this.config;
		let isValid = true;
		isValid = validation
			? this.config.validation(value)
			: !required || Boolean(value);
		this.config.$validationStatus = isValid
			? ValidationStatus.success
			: ValidationStatus.error;
		this.paint();
		return isValid;
	}
	clearValidate(): void {
		this.config.$validationStatus = ValidationStatus.pre;
		this.paint();
	}
	setValue(value: string | Date): void {
		this.timepicker.setValue(value);
		if (this.config.actions) {
			this.timepicker.events.fire(TimepickerEvents.save, []);
		}
		this.paint();
	}
	setConfig(config: ITimeInput): void {
		if (isEmptyObj(config)) {
			return;
		}
		if (this.timepicker) {
			this.timepicker.destructor();
		}
		if (this._popup) {
			this._popup.destructor();
		}
		this.config = {
			type: this.config.type,
			id: this.config.id,
			name: this.config.name,
			value: ""
		};
		for (const key in config) {
			if (key !== "id" && key !== "type" && key !== "name") {
				this.config[key] = config[key];
			}
		}
		this._popup = new Popup({css: "dhx_widget--border-shadow"});
		this.timepicker = new Timepicker(null, config);
		this._popup.attach(this.timepicker);

		if (this.config.value) {
			this.timepicker.setValue(this.config.value);
			this.config.value = this.timepicker.getValue();
		}
		if (this.config.actions) {
			this.timepicker.events.on(TimepickerEvents.close, () => {
				this._popup.hide();
			});
			this.timepicker.events.on(TimepickerEvents.save, () => {
				this.config.value = this.timepicker.getValue() as string;
				this.validate();
				this._popup.hide();
			});
			this._popup.events.on(PopupEvents.afterHide, () => {
				const { value } = this.config;
				if (value && value !== this.timepicker.getValue()) {
					this.timepicker.setValue(value);
				}
				if (value === "") {
					this.clear();
				}
				this.paint();
			});
		} else {
			this.timepicker.events.on(TimepickerEvents.change, () => {
				this.config.value = this.timepicker.getValue() as string;
				this.validate();
			});
			this._popup.events.on(PopupEvents.afterHide, () => {
				this.paint();
			});
		}
		this.events.on(BaseElementEvent.change, value => {
			const { timeFormat } = this.config;
			const length = timeFormat === 12 ? 7 : 5;
			if (value.length >= length) {
				const validValue = this._inputValidate(value);
				if (this._inputValidate(value)) {
					this.timepicker.setValue(validValue);
				} else {
					this.validate();
				}
			}
		});
		this.events.fire(BaseElementEvent.configUpdate, [this.config]);
		this.clearValidate();
	}
	getValue(): string {
		return this.config.value || "";
	}
	clear(): void {
		const timeFormat = this.config.timeFormat;
		timeFormat === 12 ? this.timepicker.setValue("12:00AM") : this.timepicker.setValue("00:00");
		this.config.value = "";
	}
	getWidget(): Timepicker {
		return this.timepicker;
	}
	protected _getHandlers() {
		return {
			onfocus: () => {
				if (this._popup.isVisible()) {
					return;
				}
				const node = this.getRootView().refs.input.el;
				this._popup.show(node);
			},
			onkeyup: (e: KeyboardEvent) => {
				const value = (e.target as HTMLInputElement).value.trim();
				if (!this.config.actions && e.keyCode === 13) {
					if (this._popup.isVisible()) {
						this._popup.hide();
					}
					const node = this.getRootView().refs.input.el;
					node.blur();
				}
				if ((e.which >= 48 && e.which <= 57) || (e.which >= 65 && e.which <= 90) || (e.which >= 96 && e.which <= 105)) {
					this.events.fire(BaseElementEvent.change, [value]);
				}
			}
		};
	}
	protected _inputValidate(value: string): string {
		const { timeFormat } = this.config;
		if (isTimeFormat(value, timeFormat)) {
			return value;
		}
		return "";
	}
	protected _draw() {
		const { value, required, disabled, placeholder, name, id, validation, editing = false } = this.config;
		return el("div.dhx_form-group", {
			class: getFormItemCss(this.config, Boolean(required) || Boolean(validation)),
		}, [
			this._drawLabel(),
			el(".dhx_input__wrapper",
			[
				el("div.dhx_input__container", {
				},[
					el(".dhx_input__icon.dxi.dxi-clock-outline"),
					el("input.dhx_input.dhx_input--icon-padding", {
						_key: this._uid,
						value,
						type: "text",
						_ref: "input",
						required,
						disabled,
						placeholder: placeholder || "",
						name: name || "",
						id: id || this._uid,
						onfocus: this._handlers.onfocus,
						onkeyup: this._handlers.onkeyup,
						autocomplete: "off",
						readOnly: !editing
					}),
				]),
				getValidationMessage(this.config) && el("span.dhx_input__caption", {
				}, getValidationMessage(this.config))
			]),
		]);
	}
}